import React, { useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'common-ui/button';
import Modal from 'common-ui/modal';
import { AsyncSelect } from 'common-ui/select';
import { createLeadB2B, createLeadB2C, updateLead } from 'features/newsales/newsalesActions';
import { getTicketLinkOptions } from 'features/projectManagement/ProjectManagementActions';

import { useLeadForm } from '../LeadFormContext';
import {
  BlueHeader,
  ErrorMessage,
  FooterActions,
  FormBody,
  HeaderSubtitle,
  HeaderTitle,
  ModalContentWrapper,
} from '../competitorModal/CompetitorModal.styles';

const AttachTicketModal = ({ isOpen, onClose, onBack, isEditMode = false, leadId = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formData, getLeadPayload, resetFormData } = useLeadForm();

  const [ticketUuid, setTicketUuid] = useState(null);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [ticketOffset, setTicketOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadTickets = async (inputValue, isLoadMore = false, offset = 0) => {
    try {
      const response = await dispatch(
        getTicketLinkOptions({ search: inputValue, limit: 10, offset })
      ).unwrap();
      const options = (response.tickets || []).map((elm) => ({
        value: elm.uuid,
        label: `(${elm.taskId}) ${elm.name}`,
      }));
      if (isLoadMore && offset > 0) {
        setTicketOptions((prev) => [...prev, ...options]);
      } else {
        setTicketOptions(options);
      }
      setHasMore(response.count > offset + 10);
      setTicketOffset(offset + 10);
      return options;
    } catch {
      return [];
    }
  };

  const handleScrollToBottom = () => {
    if (hasMore) loadTickets('', true, ticketOffset);
  };

  const handleCreate = async (selectedTicketUuid = null) => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const payload = getLeadPayload();
      const isB2B = formData.customerType === 'B2B';
      const finalPayload = {
        ...payload,
        ...(selectedTicketUuid && { ticketUuid: selectedTicketUuid }),
      };

      let response;
      if (isEditMode && leadId) {
        const editPayload = { ...finalPayload, businessType: formData.customerType };
        response = await dispatch(updateLead({ leadId, leadData: editPayload })).unwrap();
        resetFormData();
        setTicketUuid(null);
        onClose();
        navigate(`/lead-info?id=${leadId}`);
      } else {
        if (isB2B) {
          response = await dispatch(createLeadB2B(finalPayload)).unwrap();
        } else {
          response = await dispatch(createLeadB2C(finalPayload)).unwrap();
        }
        resetFormData();
        setTicketUuid(null);
        onClose();
        if (response?.id) {
          navigate(`/lead-info?id=${response.id}`);
        }
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      const errorMsg =
        error?.message ||
        error?.data?.message ||
        error?.error?.message ||
        (typeof error === 'string' ? error : null) ||
        (isEditMode
          ? 'Failed to update lead. Please try again.'
          : 'Failed to create lead. Please try again.');
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} $noPadding={true} width="510px" footer={false}>
      <ModalContentWrapper>
        <BlueHeader>
          <HeaderTitle>Attach Ticket</HeaderTitle>
          <HeaderSubtitle>This field is optional</HeaderSubtitle>
        </BlueHeader>

        <FormBody>
          <AsyncSelect
            value={ticketUuid}
            onChange={setTicketUuid}
            label="Ticket"
            placeholder="Search for a ticket"
            loadOptions={loadTickets}
            maxMenuHeight={100}
            onMenuOpen={() => loadTickets('', false, 0)}
            onMenuScrollToBottom={handleScrollToBottom}
            defaultOptions={ticketOptions}
            isClearable
            cacheOptions
            getOptionValue={(option) => option.value}
          />
        </FormBody>

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <FooterActions>
          <Button
            className="modal-button-cancel"
            outlined
            onClick={onBack}
            disabled={isSubmitting}
          >
            Back
          </Button>
          <Button
            className="modal-button-save"
            secondary
            onClick={() => handleCreate(ticketUuid?.value || null)}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? isEditMode
                ? 'Saving...'
                : 'Creating...'
              : isEditMode
                ? 'Save Changes'
                : 'Create Lead'}
          </Button>
        </FooterActions>
      </ModalContentWrapper>
    </Modal>
  );
};

export default AttachTicketModal;
